import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react';
import { Slot } from '@radix-ui/react-slot';
import cn from 'classnames';
import { forwardRef, useEffect } from 'react';
import { mergeRefs } from 'react-merge-refs';

import { usePopupContext } from '../popup';

import type { ForwardedRef, ReactElement } from 'react';

import type { PopupContentProps } from './popup-content.types';

import './popup-content.scss';

/**
 * The `PopupContent` component is used to define the content that will be displayed inside the pop-up when it is open.
 * This could be any valid JSX, including text, forms, images, or other components.
 *
 * ### Usage
 * Place the `PopupContent` component inside the `Popup` component to define the content of the popup.
 *
 * ```tsx
 * import { Popup, PopupTrigger, PopupContent } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return (
 *     <Popup>
 *       <PopupTrigger>Trigger</PopupTrigger>
 *       <PopupContent>Content</PopupContent>
 *     </Popup>
 *   );
 * }
 * ```
 */
const PopupContent = forwardRef(
  (props: PopupContentProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): null | ReactElement => {
    const {
      asChild = false,
      children,
      className,
      initialFocus = 0,
      keepMounted = false,
      modalFocus,
      positionElement,
      ...rest
    } = props;

    const popupContext = usePopupContext();
    if (popupContext == null) {
      throw new Error('Popup components must be wrapped in <Popup />');
    }

    const { context: floatingContext, interactions, isOpen, refs } = popupContext;
    const ref = mergeRefs([refs.setFloating, forwardedRef]);

    useEffect(() => {
      refs.setPositionReference(positionElement || null);
    }, [positionElement, refs]);

    if (!keepMounted && !isOpen) {
      return null;
    }

    const Root = asChild ? Slot : 'div';

    return (
      <FloatingPortal>
        <FloatingFocusManager
          closeOnFocusOut={false}
          context={floatingContext}
          initialFocus={initialFocus}
          modal={modalFocus}
        >
          <Root
            {...interactions.getFloatingProps(rest)}
            className={cn('rbui-popup-content', { 'rbui-popup-content--closed': keepMounted && !isOpen }, className)}
            ref={ref}
            style={{
              ...floatingContext.floatingStyles,
              ...((interactions.getFloatingProps(rest)?.style as object) && {}),
            }}
          >
            {children}
          </Root>
        </FloatingFocusManager>
      </FloatingPortal>
    );
  },
);

PopupContent.displayName = 'PopupContent';

export default PopupContent;
